import React, { useState, useEffect, useContext, useMemo } from "react";
import { ModalWrapper } from "../../../../components/ui";

import { DispatchContext } from "../../../../context/domain/planning/DispatchContext";

import {
  completeDispatch,
  getDispatchSmsPreview,
} from "../../../../services/api/dispatch";
import { Button } from "../../../../components/ui";
import { SearchableDropdown } from "../../../../components/ui";
import { TextMessagePreview } from "../../../../components/ui";
import SMSCostWarning from "./SMSCostWarning";

import { X } from "../../../../components/icons/svg/outline";

import { isDefaultLanguage, isSupportedLanguage } from "../../../../config";
import {
  compareWorkersByName,
  getWorkerDisplayLabel,
  prefersNonDefaultLanguage,
} from "../../../../utils/business";
import { useSmsTranslationPreviews } from "./useSmsTranslationPreview";
import { formatDate, getDateString } from "../../../../utils/datetime";
import ReviewResponseModal from "./ReviewResponseModal";

const TITLE_ID = "dispatch-sms-modal-title";

/**
 * DispatchModal component for displaying and sending SMS messages to selected workers.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Date} props.date - The date for which the SMS messages are being sent.
 * @param {Object} props.selectedWorkers - Workers to whom the SMS messages are sent.
 * @param {boolean} props.isOpen - Flag indicating whether the modal is open.
 * @param {function} props.onClose - Function to call when modal is closed (X button).
 * @returns {JSX.Element|null} The rendered component or null if the modal is not open.
 */
const DispatchModal = ({ date, selectedWorkers, isOpen, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [previewError, setPreviewError] = useState("");
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [selectedWorker, setSelectedWorker] = useState("");
  const [messageText, setMessageText] = useState("");
  const [response, setResponse] = useState(null); // Change state to store the entire response
  const [isSending, setIsSending] = useState(false);
  const [isResponseOpen, setIsResponseOpen] = useState(false);
  const { dayDetails, reloadDays, runPlanningOperation } =
    useContext(DispatchContext);
  const dateKey = getDateString(date);

  useEffect(() => {
    if (isOpen) {
      setIsSending(false);
    }
  }, [isOpen]);

  // Preview text comes from the same builder the send uses.
  useEffect(() => {
    if (!isOpen || !dateKey) {
      return undefined;
    }
    let cancelled = false;
    setPreviewError("");
    setIsPreviewLoading(true);
    setMessages([]);
    setSelectedWorker("");
    setMessageText("");
    getDispatchSmsPreview(dateKey)
      .then((data) => {
        if (!cancelled) {
          setMessages(data.messages || []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPreviewError("Could not load the message preview.");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsPreviewLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, dateKey]);

  const sortedMessages = useMemo(
    () => [...messages].sort((a, b) => compareWorkersByName(a.worker, b.worker)),
    [messages]
  );

  const previewWorkerOptions = useMemo(
    () =>
      sortedMessages.map((message, index) => ({
        // Index, not worker id: one worker can have a different text per scope.
        value: String(index),
        label: getWorkerDisplayLabel(message.worker, { includeLanguage: true }),
      })),
    [sortedMessages]
  );

  const selectedMessage =
    selectedWorker === "" || selectedWorker == null
      ? null
      : sortedMessages[Number(selectedWorker)] || null;
  const previewWorker = selectedMessage?.worker;
  const previewLanguage = (() => {
    const code = String(previewWorker?.preferred_language || "").trim().toLowerCase();
    if (!code || isDefaultLanguage(code) || !isSupportedLanguage(code)) {
      return "";
    }
    return code;
  })();
  const translatedPreviews = useSmsTranslationPreviews(messageText, {
    enabled: isOpen && Boolean(previewLanguage),
    targetLanguages: previewLanguage ? [previewLanguage] : [],
  });
  // One SMS per scope assignment, plus a translated follow-up when the language is not English.
  // Prefer the server preview so the count matches what Complete Dispatch will send.
  const pricedWorkers = messages.length
    ? messages.map((row) => row.worker)
    : selectedWorkers?.allWorkers || [];
  const smsMessageCount =
    pricedWorkers.length + pricedWorkers.filter(prefersNonDefaultLanguage).length;

  if (!isOpen) return null;

  /**
   * Load the SMS preview text for the worker chosen in the dropdown.
   *
   * @param {string|number|null} workerId Selected worker id.
   * @returns {void}
   */
  const handlePreviewWorkerChange = (workerId) => {
    const index = workerId == null || workerId === "" ? "" : String(workerId);
    setSelectedWorker(index);
    const message = index === "" ? null : sortedMessages[Number(index)];
    setMessageText(message ? message.message : "");
  };

  /**
   * Run Complete Dispatch on the server, then show the SMS result.
   *
   * Texts, the board archive, and the Dispatched stage are one backend call.
   * The planning wrapper still records the sync event other modules listen for.
   *
   * @param {void}
   * @returns {Promise<void>}
   */
  const handleSendMessagesClick = async () => {
    setIsSending(true);
    try {
      const result = await runPlanningOperation({
        module: "dispatch",
        operation: "markDayDispatched",
        resources: ["dispatch.dispatch_stage"],
        apiCall: () => completeDispatch(date),
      });
      setResponse(result);
      setIsResponseOpen(true);
      await reloadDays();
    } catch (error) {
      setResponse({
        error: error.response?.data?.error || "Complete dispatch failed",
        errors: error.response?.data?.errors || [],
        success: error.response?.data?.success || [],
        capture_error: error.response?.data?.capture_error,
      });
      setIsResponseOpen(true);
      try {
        await reloadDays();
      } catch (reloadError) {
        console.error(reloadError);
      }
    } finally {
      setIsSending(false);
    }
  };

  // Close the modal and reset the states
  /**
   * Close the modal and reset preview/response state.
   *
   * @param {void}
   * @returns {void}
   */
  const handleClose = () => {
    onClose();
    setResponse(null);
    setIsResponseOpen(false);
    setMessageText("");
    setSelectedWorker("");
  };

  const dispatchDetails = dayDetails.filter(
    (detail) => detail.date === getDateString(date)
  )[0];
  const dispatchStages = dispatchDetails["dispatch_stage"];

  var alreadyDispatched = [];
  if (dispatchStages) {
    alreadyDispatched = dispatchStages.filter(
      (detail) => detail.stage === "Dispatched"
    );
  }

  return (
    <>
      {selectedWorkers && dayDetails && (
        <ModalWrapper
          isOpen={isOpen}
          onClose={handleClose}
          closeButton={false}
          maxWidth={800}
          className="relative w-full max-h-[90vh] overflow-y-auto rounded-xl bg-white p-0 shadow-xl"
          outsideClickIgnoreSelector="[data-searchable-dropdown-listbox]"
        >
          <div role="dialog" aria-modal="true" aria-labelledby={TITLE_ID}>
            <div className="flex items-center justify-between gap-3 bg-primary px-5 py-3.5">
              <div className="flex items-center gap-2.5">
                <h2
                  id={TITLE_ID}
                  className="text-lg font-semibold leading-none tracking-tight text-white"
                >
                  SMS Schedule
                </h2>
                <p className="text-sm leading-none text-white/80">
                  {formatDate(date, "weekday MM/DD/YY")}
                </p>
              </div>
              <button
                type="button"
                onClick={handleClose}
                className="inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded text-primary-100 transition hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/30"
                aria-label="Close"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </div>

            <div className="space-y-2 px-5 py-4 pt-2">
              {alreadyDispatched.length > 0 && !isResponseOpen ? (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-700">
                  <p className="font-semibold">
                    SMS messages have already been sent for this day
                  </p>
                  <ul className="mt-1.5 list-disc space-y-0.5 pl-5">
                    {alreadyDispatched.map((detail, index) => {
                      const time = detail.time.split(":").slice(0, 2).join(":");
                      return (
                        <li key={index}>
                          {detail.date}, {time} (
                          {detail.user
                            ? `${detail.user.first} ${detail.user.last}`
                            : "RSApp Auto-Complete Dispatch"}
                          )
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : null}
              {dayDetails.sms_author && dayDetails.sms_time ? (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-700">
                  SMS scheduling messages have already been sent for this day by{" "}
                  <span className="font-semibold">
                    {dayDetails.sms_author.first} {dayDetails.sms_author.last}
                  </span>{" "}
                  at{" "}
                  <span className="font-semibold">
                    {dayDetails.sms_time.slice(0, 5)}
                  </span>
                  .
                </div>
              ) : null}

              <section>
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <h3 className="text-sm font-semibold text-gray-800">
                    Recipients
                  </h3>
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                    {selectedWorkers.allWorkers.length}{" "}
                    {selectedWorkers.allWorkers.length === 1
                      ? "recipient"
                      : "recipients"}
                  </span>
                </div>
                <div className="rounded-lg border border-gray-200 bg-gray-50/80 px-3 py-2">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                    {[...selectedWorkers.allWorkers]
                      .sort(compareWorkersByName)
                      .map((worker, index) => (
                        <p
                          key={`sms-worker-${index}`}
                          className="text-sm text-gray-800"
                        >
                          {getWorkerDisplayLabel(worker, { includeLanguage: true })}
                        </p>
                      ))}
                  </div>
                </div>
              </section>

              <section>
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold text-gray-800">
                    Message
                  </h3>
                  <SearchableDropdown
                    options={previewWorkerOptions}
                    value={selectedWorker || null}
                    onChange={handlePreviewWorkerChange}
                    placeholder="Select a worker to preview..."
                    ariaLabel="Preview worker message"
                    searchWhenOverflow
                    className="w-56"
                    listboxClassName="z-[10060]"
                  />
                </div>
                {messageText ? (
                  <div className="rounded-lg bg-gray-50 px-3 py-3">
                    <TextMessagePreview
                      text={messageText}
                      extraTexts={translatedPreviews}
                      label="Sample Message Preview"
                    />
                  </div>
                ) : previewError ? (
                  <p className="rounded-lg border border-dashed border-red-200 px-3 py-6 text-center text-sm text-red-600">
                    {previewError}
                  </p>
                ) : (
                  <p className="rounded-lg border border-dashed border-gray-200 px-3 py-6 text-center text-sm text-gray-400">
                    {isPreviewLoading
                      ? "Loading the messages that will be sent."
                      : "Select a worker to preview the message they will receive."}
                  </p>
                )}
              </section>

              <SMSCostWarning messageCount={smsMessageCount} />
              <Button
                variant={isSending ? "disabled" : "secondary"}
                text={isSending ? "Sending..." : "Send All Messages"}
                onClick={handleSendMessagesClick}
                disabled={isSending}
                className="w-full rounded-lg"
              />
            </div>
          </div>
        </ModalWrapper>
      )}
      <ReviewResponseModal
        response={response}
        isOpen={isResponseOpen}
        setIsOpen={setIsResponseOpen}
        onClose={handleClose}
      />
    </>
  );
};

export default DispatchModal;
